"use client";

import { motion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const trustHighlights = [{
  icon: UserCheck,
  title: "Direct specialist access",
  description: "Collaborate directly with the practitioner managing and executing your search strategy."
}, {
  icon: ShieldCheck,
  title: "Tailored strategy",
  description: "Custom search roadmaps built for your specific market."
}, {
  icon: CheckCircle2,
  title: "Transparent reporting",
  description: "Clear deliverables, measurable rankings, and honest ROI."
}];
export default function Hero() {
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Hero Copy Column */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }}>
              <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-medium bg-secondary text-secondary-foreground border border-border/60 rounded-full">
                Independent SEO Consultant
              </Badge>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.1,
            ease: "easeOut"
          }} className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-foreground leading-[1.12]">
              SEO Agency Alternative UK
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.2,
            ease: "easeOut"
          }} className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed font-sans">
              An independent alternative to conventional agency retainers. I provide UK and international businesses with focused technical SEO, content strategy, and sustainable organic search revenue.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.55,
            delay: 0.3,
            ease: "easeOut"
          }} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium px-6 py-6 text-base">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild variant="secondary" size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border font-medium px-6 py-6 text-base">
                <Link to="/SeoAuditsPage">Get a Free SEO Audit</Link>
              </Button>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.45
          }} className="mt-10 pt-8 border-t border-border/80 grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
              {trustHighlights.map((item, i) => {
              const Icon = item.icon;
              return <div key={item.title} data-index={i} className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Icon className="size-4 text-primary shrink-0" />
                      <span>{item.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>;
            })}
            </motion.div>
          </div>

          {/* Consultant Visual Card Column */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.25,
          ease: "easeOut"
        }} className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-4 pb-6 border-b border-border">
                <div className="relative size-16 sm:size-20 rounded-full overflow-hidden border-2 border-primary/20 bg-muted shrink-0">
                  <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent UK SEO Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-card-foreground font-serif">
                    Yassine Essaadi
                  </h2>
                  <p className="text-sm text-primary font-medium">
                    Freelance SEO Consultant
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    YE Consultancy - Swindon, UK
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3.5 text-sm">
                <div className="flex items-start gap-2.5">
                  <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground font-medium">Hands-on delivery:</strong> Every technical audit, code recommendation, and content roadmap is researched and implemented without subcontracting.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground font-medium">Flexible, clearly defined deliverables:</strong> Transparent scopes focused on measurable rankings and conversion progress.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground font-medium">AI &amp; Google search readiness:</strong> Modern visibility strategies covering both Google search and generative AI engines.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Based in Wiltshire &amp; serving clients UK-wide</span>
                <span className="text-primary font-semibold">1-to-1 Focus</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}